/* eslint-disable prettier/prettier */
/* eslint-disable no-console */
/* eslint-disable no-restricted-syntax */
import type { EasemobChat } from 'easemob-websdk';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BASE_IM_CONFIG, APP_KEY } from '../constants/im';
import { getCommonParams, OS_TYPE } from '../request/params';
import { clearImToken, initImToken, useAppDispatch } from '../state';
import {
  AttachmentImage,
  AudioStatus,
  EAsideScene,
  ENpcAgentType,
  IAsideMsgInfo,
  IMessage,
  MessageType,
  RoleType,
} from '../types';
import { isProd } from '../utils/env';
import { miliSecondToSecond } from '../utils';
export const EASEMOB_OTHER_USER_LOGGED_CODE = 206;
export const EASEMOB_NOT_LOGIN_CODE = 39;
export const EASEMOB_TOKEN_INVALID = 1;
export const EASEMOB_AUTH_FAIL = 2;
export const EASEMOB_INVALID_TOKEN = 28;
export const EASEMOB_CUSTOM_ERROR_CODE = 502;

export enum ECustomStatusCode {
  closeDoor = 1105050061,
}

const LOADING_TIMEOUT = 30 * 1000; // loading超时时间30s

export const SENSITIVE_ERROR_CODE = [502, 508];

export enum CustomEvent {
  Text = 'text',
  Voice = 'voice',
  Aside = 'aside',
  Attachment = 'attachment',
}

export interface IInitIMProps {
  imAccount: string;
  imPassword: string;
  imToken?: string;
}

export interface ICustomExt {
  need_loading: boolean;
  reply_for: string;
  is_prologue: boolean;
  role_play_role_id?: number;
}

export interface ITextCustomExts {
  customExts: { msg: string; voice_duration_ms: number };
}

export interface CustomError {
  type: number;
  message: string;
  status_code?: number;
}

interface IInitIMEventHandlerProps {
  onInitSuccess: () => void;
  onInitError: (error: EasemobChat.ErrorEvent) => void;
  onReceiveTextMessage: (data: IMessage, ext?: ICustomExt) => void;
  onReceiveVoiceMessage: (data: Partial<IMessage>, ext?: ICustomExt) => void;
  onReceiveCMDMessage?: (data: EasemobChat.CmdMsgBody, ext?: ICustomExt) => void;
  onReceiveCustomMessage?: (data: EasemobChat.CustomMsgBody, ext?: ICustomExt) => void;
  onReceiveAsideMessage: (data: IMessage) => void;
  onSendMessageSuccess: (mid: string, localMid: string) => void;
  onSendMessageError: (error: CustomError, mid: string) => void;
  onSendCmdMessage: (data: any) => void;
  onModifiedMessage?: (data: IMessage) => void;
  onConnected?: () => void;
  onGetHistoryMessagesSuccess?: (messages: IMessage[]) => void;
  onGetHistoryMessagesError?: (error: any) => void;
}

export interface IUseIMValues {
  initIM: (data: IInitIMProps) => void;
  initIMEventHandler: (data: IInitIMEventHandlerProps) => void;
  setNpcImAccount: (account: string) => void;
  sendMessage: (txt: string, mid: string, attachment?: AttachmentImage | null) => void;
  getHistoryMessages: (id: string) => void;
  loadingCnt: number;
  hasLoading: boolean;
  setLoadingCnt: any;
  isSyncing: boolean;
  npcAgentType: MutableRefObject<ENpcAgentType>;
}

const extractImageFromCustomExts = (customExts: string) => {
  const parsed = JSON.parse(customExts || '{}');
  return parsed?.image || {};
};

const useIM = ({
  loadingStep = 1,
  timeout = LOADING_TIMEOUT,
  app,
  fromWorkspace,
}: {
  loadingStep?: number;
  timeout?: number;
  app: 'xingye' | 'talkie';
  fromWorkspace?: boolean;
}): IUseIMValues => {
  // const [npcImAccount, setNpcImAccount] = useState<string>('');
  const [loadingCnt, setLoadingCnt] = useState<number>(0);

  const eventHandler = useRef<IInitIMEventHandlerProps>({} as IInitIMEventHandlerProps);

  const loadingTimer = useRef<any>(null);

  const conn = useRef<EasemobChat.Connection>();

  const hasLoading = useMemo(() => loadingCnt > 0, [loadingCnt]);

  const npcImAccount = useRef('');

  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  const handleAITextMessage = (message: EasemobChat.TextMsgBody) => {
    const { id, customExts, ext } = message as EasemobChat.TextMsgBody & ITextCustomExts;
    const { reply_for, is_prologue } = ext as ICustomExt;

    return {
      txt: customExts?.msg,
      role_type: RoleType.AI,
      type: MessageType.Text,
      mid: id, // 消息id
      voice_duration_ms: customExts ? miliSecondToSecond(customExts?.voice_duration_ms) : undefined,
      reply_for,
      is_open: is_prologue,
    };
  };

  const onTextMessage = useCallback(
    (message: EasemobChat.TextMsgBody) => {
      // eslint-disable-next-line no-console, no-restricted-syntax
      console.debug('on easemob 【text】 message receive', message);

      if (message.from !== npcImAccount.current) return;
      const { need_loading } = message.ext as ICustomExt;

      try {
        const _message = handleAITextMessage(message);

        setLoadingCnt((cnt) => cnt - 1 || 0);

        if (need_loading) {
          setLoadingCnt((cnt) => cnt + loadingStep);
        }
        eventHandler.current?.onReceiveTextMessage?.(_message);
      } catch (err) {
        console.error('on easemob 【text】 message receive', err);
      }
    },
    [loadingStep]
  );

  // 处理 type: 'custom' 的 voice 消息（npc消息）
  const handleCustomVoiceMessage = (message: EasemobChat.CustomMsgBody) => {
    const { id, customExts, ext } = message;
    const { reply_for, is_prologue } = ext as ICustomExt;

    return {
      txt: customExts?.msg,
      role_type: RoleType.AI,
      type: MessageType.Text,
      mid: id,
      voice_uri: customExts?.voice_uri,
      voice_duration_ms: miliSecondToSecond(customExts?.voice_duration_ms),
      wav_status: AudioStatus.Ready,
      reply_for,
      is_open: is_prologue,
    };
  };

  const onCustomMessage = useCallback(
    (message: EasemobChat.CustomMsgBody) => {
      // eslint-disable-next-line no-console, no-restricted-syntax
      console.debug('on easemob 【custom】 message receive', message);
      if (message.from !== npcImAccount.current) return;

      const { id, customEvent, customExts, ext } = message;
      const { need_loading, reply_for } = ext as ICustomExt;

      try {
        if (customEvent === CustomEvent.Text || customEvent === CustomEvent.Voice) {
          const _message = handleCustomVoiceMessage(message);

          setLoadingCnt((cnt) => cnt - 1 || 0);

          if (need_loading) {
            setLoadingCnt((cnt) => cnt + loadingStep);
          }
          eventHandler.current?.onReceiveTextMessage(_message, ext as ICustomExt);
        } else if (customEvent === CustomEvent.Aside) {
          // 旁白参考文档 https://vrfi1sk8a0.feishu.cn/docx/UhQadYDJZoqe7Cx9oCpcKlKNnFd
          // web只处理4种旁白，其他直接忽略
          const body = JSON.parse(customExts?.aside_msg_info || '{}') as IAsideMsgInfo;

          const { text_info, aside_scene } = body || {};
          if (
            ![
              EAsideScene.GenerateReplyFail,
              EAsideScene.SensitiveReply,
              EAsideScene.SensitiveWarning,
              EAsideScene.ThrowMsg,
            ].includes(aside_scene)
          ) {
            return;
          }

          setLoadingCnt((cnt) => cnt - 1 || 0);
          const _message = {
            role_type: RoleType.AI,
            mid: id,
            type: MessageType.Aside,
            reply_for,
            txt: '',
            text_info,
          };

          eventHandler.current?.onReceiveAsideMessage(_message);
        } else if (customEvent === CustomEvent.Attachment) {
          setLoadingCnt((cnt) => cnt - 1 || 0);
          const _message = {
            role_type: RoleType.AI,
            mid: id,
            type: MessageType.Attachment,
            reply_for,
            txt: '',
            attachment: extractImageFromCustomExts(customExts?.attachment || '{}'),
          };
          eventHandler.current.onReceiveTextMessage?.(_message, ext as ICustomExt);
        } else {
          eventHandler.current.onReceiveCustomMessage?.(message);
        }
      } catch (err) {
        console.error('on easemob 【custom】 message error', err);
      }
    },
    [loadingStep]
  );

  const onCmdMessage = useCallback((message: EasemobChat.CmdMsgBody) => {
    // eslint-disable-next-line no-console, no-restricted-syntax
    console.debug('on easemob 【cmd】 message receive', message);

    if (message.action) {
      try {
        const body = JSON.parse(message.action);
        if (body?.type === 'web_load_share_popup') {
          eventHandler.current?.onSendCmdMessage(body);
        }
      } catch (err) {
        console.error('on easemob 【cmd】 message handler error', err);
      }
    }

    if (message.from !== npcImAccount.current) return;

    const { action, ext } = message;

    try {
      const body = JSON.parse(action);

      if (body.is_success) {
        const { ease_msg_id, voice_uri, voice_duration_ms } = body;
        const _message = {
          mid: ease_msg_id,
          voice_uri,
          voice_duration_ms: miliSecondToSecond(voice_duration_ms),
        };

        eventHandler.current?.onReceiveVoiceMessage(_message, ext as ICustomExt);
      }
      eventHandler.current?.onReceiveCMDMessage?.(message, ext as ICustomExt);
    } catch (err) {
      console.error('on easemob 【cmd】 message handler error', err);
    }
  }, []);

  const dispatch = useAppDispatch();

  const initIM = useCallback(
    ({ imAccount, imPassword, imToken }: IInitIMProps) => {
      if (conn.current) return;

      import('easemob-websdk').then(({ default: WebIM }) => {
        const imEnv = isProd ? 'prod' : 'test';
        conn.current = new WebIM.connection({
          ...BASE_IM_CONFIG,
          appKey: APP_KEY[app][imEnv],
        });
        // console.log('conn.current', conn.current.clientResource)

        const openConnection = (options: any) => {
          conn.current?.open(options).then(({ accessToken }) => {
            dispatch(initImToken(accessToken));
          });
        };

        try {
          console.time('easeMob Connection');
          const imAccountOptions = {
            user: imAccount,
            pwd: imPassword,
            success: eventHandler.current.onInitSuccess,
            error: eventHandler.current.onInitError,
          };
          const errorOrRetry = (err: EasemobChat.ErrorEvent) => {
            if (
              err.type === EASEMOB_TOKEN_INVALID ||
              err.type === EASEMOB_AUTH_FAIL ||
              err.type === EASEMOB_INVALID_TOKEN
            ) {
              console.debug('on easemob 【connection】 token invalid', err);
              openConnection(imAccountOptions);
            } else eventHandler.current.onInitError(err);
          };
          const imTokenOptions = {
            user: imAccount,
            accessToken: imToken,
            success: eventHandler.current.onInitSuccess,
            error: errorOrRetry,
          };
          const options = imToken ? imTokenOptions : imAccountOptions;
          openConnection(options);
          conn.current.addEventHandler('connection&message', {
            onConnected: () => {
              console.timeEnd('easeMob Connection');
              eventHandler.current.onInitSuccess();
              eventHandler.current.onConnected?.();
            },
            onTextMessage,
            onCustomMessage,
            onCmdMessage,
            onError: options.error,
            onTokenWillExpire: () => {
              if (!imToken) return;
              conn.current
                ?.renewToken(imToken)
                .then((value) => {
                  if (value.token) dispatch(initImToken(value.token));
                })
                .catch(() => {
                  dispatch(clearImToken());
                });
            },
            onTokenExpired: () => {
              dispatch(clearImToken());
            },
          });
          conn.current.addEventHandler('modifiedMessage', {
            onModifiedMessage(msg) {
              if (msg.type === 'custom' && msg.customEvent === CustomEvent.Attachment) {
                const _message = {
                  role_type: RoleType.AI,
                  mid: msg.id,
                  txt: '',
                  attachment: extractImageFromCustomExts(msg.customExts?.attachment || '{}'),
                };
                eventHandler.current?.onModifiedMessage?.(_message as any);
              }
            },
          });
        } catch (err) {
          console.error('easemob 【init】 im error', err);
          eventHandler.current?.onInitError(err as EasemobChat.ErrorEvent);
        }
      });
    },
    [app, dispatch, onTextMessage, onCustomMessage, onCmdMessage]
  );

  const initIMEventHandler = (handlers: IInitIMEventHandlerProps) => {
    eventHandler.current = handlers;
  };

  const npcAgentType = useRef<ENpcAgentType>(ENpcAgentType.SingleChat);

  const sendMessage = useCallback(
    (txt: string, mid: string, attachment?: AttachmentImage | null) => {
      const commonParams = getCommonParams();
      const extParams = {
        os: OS_TYPE,
        npc_agent_type: npcAgentType.current,
        from_workspace: fromWorkspace,
        // em_webhook_pre_header: JSON.stringify({
        //   lane: 'npc-desc',
        // }),
        common_param: {
          ...commonParams,
          version_code: 0,
          version_name: '',
          sys_language: '',
          sys_region: '',
        },
      };

      import('easemob-websdk').then(({ default: WebIM }) => {
        const msg = attachment
          ? WebIM.message.create({
              chatType: 'singleChat',
              type: 'custom',
              to: npcImAccount.current,
              ext: extParams,
              customEvent: CustomEvent.Attachment,
              customExts: {
                msg: txt,
                attachment: JSON.stringify({ image: attachment }),
              },
            })
          : WebIM.message.create({
              chatType: 'singleChat',
              type: 'txt',
              to: npcImAccount.current,
              msg: txt,
              ext: extParams,
            });

        conn.current
          ?.send(msg)
          .then((res: EasemobChat.SendMsgResult) => {
            // eslint-disable-next-line no-console, no-restricted-syntax
            console.debug('on easemob 【sendMesage】 message', res);
            setLoadingCnt((cnt) => cnt + loadingStep);
            eventHandler.current?.onSendMessageSuccess(res.serverMsgId, mid);
          })
          .catch((err: CustomError) => {
            console.error('on easemob 【sendMesage】 error', err);
            const { type, message } = err || {};
            const _err: CustomError = {
              type,
              message: 'internal error',
            };
            try {
              if (type === EASEMOB_CUSTOM_ERROR_CODE) {
                // 命中发送前回调error，尝试parse message obj
                const obj = JSON.parse(message);
                _err.message = obj.text;
                _err.status_code = obj.status_code;
              } else {
                _err.message = message;
              }
            } catch (err) {
              _err.message = message;
            }
            eventHandler.current?.onSendMessageError(_err, mid);
          });
      });
    },
    [loadingStep]
  );

  const handleUserTextMessage = (message: EasemobChat.TextMsgBody) => {
    const { id, ext, msg } = message as EasemobChat.TextMsgBody;
    const { reply_for } = ext as ICustomExt;

    return {
      txt: msg,
      role_type: RoleType.User,
      type: MessageType.Text,
      mid: id, // 消息id
      reply_for,
      is_open: false,
    };
  };

  const getHistoryMessages = useCallback((id: string) => {
    setIsSyncing(true);
    const historyMessages: EasemobChat.MessagesType[] = [];

    const getHistoryByCursor = (cursor: string, data: EasemobChat.MessagesType[]) => {
      conn.current
        ?.getHistoryMessages({
          targetId: id,
          chatType: 'singleChat',
          pageSize: 20,
          cursor,
          searchDirection: 'down',
          searchOptions: {
            msgTypes: ['txt', 'custom'], // txt 用户消息，custom npc消息，暂不使用 cmd（指令消息）
          },
        })
        .then((res: EasemobChat.HistoryMessages) => {
          // eslint-disable-next-line no-console, no-restricted-syntax
          console.debug('on easemob 【getHistoryMessages】 messages', res);
          if (res?.messages?.length >= 0) {
            data = data.concat(res.messages);
          }
          // 结束递归
          if (res.isLast || !res.cursor) {
            setIsSyncing(false);
            const _message: IMessage[] = data
              .filter(
                (msg) =>
                  msg.type === 'txt' || (msg.type === 'custom' && msg.customEvent === 'voice')
              )
              .map((msg) => {
                if (msg.type === 'txt') {
                  return handleUserTextMessage(msg as EasemobChat.TextMsgBody);
                } else {
                  return handleCustomVoiceMessage(msg as EasemobChat.CustomMsgBody);
                }
              });
            // eslint-disable-next-line no-console, no-restricted-syntax
            console.debug('on easemob 【getHistoryMessages】 messageList', _message);
            eventHandler.current?.onGetHistoryMessagesSuccess?.(_message);
          } else {
            getHistoryByCursor(res.cursor || '', data);
          }
        })
        .catch((err: EasemobChat.ErrorEvent) => {
          setIsSyncing(false);
          console.error('getHistoryMessage catch', err);
          eventHandler.current?.onGetHistoryMessagesError?.(err);
        });
    };

    getHistoryByCursor('', historyMessages);
  }, []);

  useEffect(() => {
    if (loadingCnt > 0) {
      loadingTimer.current && clearTimeout(loadingTimer.current);
      loadingTimer.current = setTimeout(() => {
        loadingTimer.current && clearTimeout(loadingTimer.current);
        setLoadingCnt(0);
      }, timeout);
    }
  }, [loadingCnt, timeout]);

  useEffect(() => {
    return () => {
      conn.current?.close();
    };
  }, []);

  const setNpcImAccount = useCallback((account: string) => {
    npcImAccount.current = account;
  }, []);

  return {
    initIM,
    initIMEventHandler,
    setNpcImAccount,
    sendMessage,
    getHistoryMessages,
    loadingCnt,
    hasLoading,
    isSyncing,
    setLoadingCnt,
    npcAgentType,
  };
};

export { useIM };
